// Pure helpers for supervised members on the web: control and mode wording,
// the controls-and-guardians form (and what saving it does), a supervision's
// status, the member's sharing as a guardian sees it, request summaries and
// decisions, the activity log and supervised invites.
import type { Schemas } from "@10twenty/api";
import { approximateText, radiusText, untilText } from "../sharing/bubbles";
import type { Units } from "../units";

type Control = Schemas["SupervisionControl"];
type Mode = Schemas["SupervisionMode"];
type Controls = Schemas["SupervisionControls"];
type Guardian = Schemas["SupervisionGuardian"];
type Supervision = Schemas["Supervision"];
type SupervisionOffer = Schemas["SupervisionOffer"];
type SupervisedMember = Schemas["SupervisedMember"];
type SupervisedSharing = Schemas["SupervisedSharing"];
type CircleRequest = Schemas["CircleSupervisionRequest"];
type RequestSummary = Schemas["SupervisionRequestSummary"];
type Action = Schemas["SupervisionAction"];
type LogEntry = Schemas["SupervisionLogEntry"];
type Member = Schemas["Member"];
type InviteCreate = Schemas["InviteCreate"];

/** Every control this app knows, in display order. */
export const CONTROLS: readonly Control[] = ["pause", "approximate", "leave", "delete_account", "join_circles"];
/** Modes from least to most strict. */
export const MODES: readonly Mode[] = ["allow", "notify", "ask"];

export const CONTROL_LABELS: Record<Control, string> = {
  pause: "Pause sharing",
  approximate: "Share an approximate area",
  leave: "Leave the circle",
  delete_account: "Delete their account",
  join_circles: "Join or create other circles",
};

export const CONTROL_HINTS: Record<Control, string> = {
  pause: "Pausing this circle, or pausing everywhere",
  approximate: "An approximate area instead of their location, for this circle or every circle",
  leave: "Admins can still remove them",
  delete_account: "Downloading their data is always allowed",
  join_circles: "Accepting invites to other circles and events, or creating one",
};

export const MODE_LABELS: Record<Mode, string> = {
  allow: "Allowed",
  notify: "Allowed, guardians are told",
  ask: "Asks a guardian first",
};

/** The supervised-invite defaults (DESIGN.md decision 4); the server uses the same for a first offer. */
export const DEFAULT_CONTROLS: Readonly<Record<Control, Mode>> = {
  pause: "ask",
  approximate: "ask",
  leave: "ask",
  delete_account: "ask",
  join_circles: "notify",
};

const RANK: Record<Mode, number> = { allow: 0, notify: 1, ask: 2 };

/** A mode per control, as the form edits it. */
export type ControlsDraft = Partial<Record<Control, Mode>>;

/** Controls and guardians, as the offer and supervised-invite forms edit them. */
export interface SupervisionDraft {
  controls: ControlsDraft;
  guardianIds: string[];
}

/** The controls a supervision in `circle` may use (none from an older server, or while supervision is off). */
export const circleControls = (circle: Pick<Schemas["Circle"], "supervision_controls">): Control[] =>
  availableControls(circle.supervision_controls ?? []);

/** The circle's controls in display order (unknown ones dropped). */
export function availableControls(available: readonly string[]): Control[] {
  return CONTROLS.filter((control) => available.includes(control));
}

/** The mode a stored supervision has for `control` (absent = allow). */
export const modeOf = (controls: Controls, control: Control): Mode => controls[control] ?? "allow";

/**
 * The form's starting modes for the circle's controls: those of `current`
 * (a running supervision or pending offer; absent = allow), else the
 * supervised-invite defaults.
 */
export function controlsDraft(available: readonly string[], current: Controls | null | undefined): ControlsDraft {
  const draft: ControlsDraft = {};
  for (const control of availableControls(available)) {
    draft[control] = current ? modeOf(current, control) : DEFAULT_CONTROLS[control];
  }
  return draft;
}

/**
 * The form's starting draft: the pending offer's (the whole supervision as
 * it would be once accepted, so a revision keeps what still waits), else
 * the running supervision's, else the defaults guarded by the caller.
 */
export function initialDraft(
  available: readonly string[],
  supervision: Pick<Supervision, "controls" | "guardians"> | null | undefined,
  offer: Pick<SupervisionOffer, "controls" | "guardians"> | null | undefined,
  selfId: string,
): SupervisionDraft {
  const source = offer ?? supervision ?? null;
  return {
    controls: controlsDraft(available, source?.controls),
    guardianIds: source ? source.guardians.map((g) => g.user_id) : [selfId],
  };
}

/** The request's `controls`: every one of the circle's controls, as drafted. */
export function controlsBody(draft: ControlsDraft, available: readonly string[]): Controls {
  const body: Controls = {};
  for (const control of availableControls(available)) body[control] = draft[control] ?? "allow";
  return body;
}

/** The controls that would get stricter and looser going from `current` to `next`. */
export function compareControls(current: Controls, next: Controls): { stricter: Control[]; looser: Control[] } {
  const stricter: Control[] = [];
  const looser: Control[] = [];
  for (const control of CONTROLS) {
    const from = RANK[modeOf(current, control)];
    const to = RANK[modeOf(next, control)];
    if (to > from) stricter.push(control);
    else if (to < from) looser.push(control);
  }
  return { stricter, looser };
}

/**
 * What saving the form does, as the server splits a revision: `offer` (the
 * member has to accept all of it), `mixed` (looser controls and dropped
 * guardians apply at once, the rest waits for the member), `now` (applies
 * at once), `withdraw` (back to the running supervision: the pending
 * changes are withdrawn) or `none` (no change). `replacesGuardians`: no
 * current guardian would remain, so they stay until the member accepts.
 */
export interface ChangeOutcome {
  kind: "offer" | "mixed" | "now" | "withdraw" | "none";
  replacesGuardians: boolean;
}

export function changeOutcome(
  current: Pick<Supervision, "controls" | "guardians"> | null | undefined,
  nextControls: Controls,
  nextGuardianIds: readonly string[],
  offerPending = false,
): ChangeOutcome {
  if (!current) return { kind: "offer", replacesGuardians: false };
  const { stricter, looser } = compareControls(current.controls, nextControls);
  const currentIds = current.guardians.map((g) => g.user_id);
  const added = nextGuardianIds.some((id) => !currentIds.includes(id));
  const kept = currentIds.filter((id) => nextGuardianIds.includes(id));
  // Dropped guardians go at once only while one of the current ones remains.
  const dropsNow = kept.length > 0 && kept.length < currentIds.length;
  const replacesGuardians = kept.length === 0 && currentIds.length > 0;
  const needsConsent = stricter.length > 0 || added;
  const atOnce = looser.length > 0 || dropsNow;
  let kind: ChangeOutcome["kind"];
  if (needsConsent) kind = atOnce ? "mixed" : "offer";
  else if (atOnce) kind = "now";
  else kind = offerPending ? "withdraw" : "none";
  return { kind, replacesGuardians };
}

/** The form's explanation of `changeOutcome` for the member called `name`. */
export function changeText(outcome: ChangeOutcome, name: string, firstOffer: boolean): string | null {
  const keep = outcome.replacesGuardians ? " The current guardians stay until then." : "";
  switch (outcome.kind) {
    case "offer":
      return firstOffer
        ? `${name} sees these controls and guardians and has to accept before supervision starts. The offer lasts 7 days.`
        : `Stricter controls and new guardians wait for ${name} to accept.${keep}`;
    case "mixed":
      return outcome.replacesGuardians
        ? `Looser controls apply at once; stricter controls and new guardians wait for ${name} to accept.${keep}`
        : `Looser controls and removed guardians apply at once; stricter controls and new guardians wait for ${name} to accept.`;
    case "now":
      return `Applies at once. ${name} is told.`;
    case "withdraw":
      return `Withdraws the changes waiting for ${name} to accept.`;
    case "none":
      return null;
  }
}

/** What an approved-here request that has since left the pending list became, as far as this page knows. */
export function closedText(request: Pick<CircleRequest, "status" | "error" | "approval">, name: string): string {
  return request.status === "pending"
    ? "No longer waiting for a decision. Activity shows what happened."
    : decisionText(request, name);
}

/** Admins of the circle who may guard `memberId`: admins who aren't supervised there, by name. */
export function eligibleGuardians<T extends Pick<Member, "user_id" | "name" | "role" | "supervised">>(
  members: readonly T[],
  memberId: string | null,
): T[] {
  return members
    .filter((m) => m.role === "admin" && !m.supervised && m.user_id !== memberId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether the draft can be sent: at least one guardian, every one still eligible. */
export function draftReady(draft: SupervisionDraft, eligible: readonly Pick<Member, "user_id">[]): boolean {
  return draft.guardianIds.length > 0 && draft.guardianIds.every((id) => eligible.some((m) => m.user_id === id));
}

/** Whether `userId` guards the running supervision. */
export function guards(entry: Pick<SupervisedMember, "supervision">, userId: string): boolean {
  return entry.supervision?.guardians.some((g) => g.user_id === userId) ?? false;
}

/** Whether `userId` guards the running supervision or the pending offer names them. */
export function involves(entry: Pick<SupervisedMember, "supervision" | "offer">, userId: string): boolean {
  return guards(entry, userId) || (entry.offer?.guardians.some((g) => g.user_id === userId) ?? false);
}

/** "Alice", "Alice and Bob", "Alice, Bob and Carol"; the caller reads as "you". */
export function namesText(people: readonly Guardian[], selfId?: string): string {
  const names = people.map((p) => (p.user_id === selfId ? "you" : p.name));
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const dateText = (iso: string, locale?: string) =>
  new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" });

/** One or two lines on where the supervision stands, for its guardians. */
export function statusLines(entry: SupervisedMember, locale?: string): string[] {
  const { supervision, offer, name } = entry;
  const lines: string[] = [];
  if (supervision) {
    lines.push(`Supervised since ${dateText(supervision.started_at, locale)}`);
    if (supervision.release_at) {
      lines.push(
        `${name} asked to stop supervision. It ends on ${dateText(supervision.release_at, locale)}, and guardians can't change that.`,
      );
    }
    if (offer) lines.push(`Changes wait for ${name} to accept (until ${dateText(offer.expires_at, locale)}).`);
  } else if (offer) {
    lines.push(`Offered · waiting for ${name} to accept (until ${dateText(offer.expires_at, locale)})`);
  }
  return lines;
}

/** The controls to list: the running supervision's, with the offered mode where an offer changes it. */
export function controlRows(
  available: readonly string[],
  supervision: Pick<Supervision, "controls"> | null | undefined,
  offer: Pick<SupervisionOffer, "controls"> | null | undefined,
): { control: Control; mode: Mode | null; offered: Mode | null }[] {
  const shown = new Set<Control>(availableControls(available));
  for (const source of [supervision, offer]) {
    if (!source) continue;
    for (const control of CONTROLS) if (source.controls[control] !== undefined) shown.add(control);
  }
  return CONTROLS.filter((control) => shown.has(control)).map((control) => {
    const mode = supervision ? modeOf(supervision.controls, control) : null;
    const offered = offer ? modeOf(offer.controls, control) : null;
    return { control, mode, offered: offered !== mode ? offered : null };
  });
}

/** Pause lengths a guardian may set, in hours; null lasts until resumed. */
export const PAUSE_CHOICES = [1, 3, 6, 24, null] as const;
export type PauseChoice = (typeof PAUSE_CHOICES)[number];

/** "1 hour", "3 hours". */
export const hoursText = (hours: number): string => (hours === 1 ? "1 hour" : `${hours} hours`);

/** "For 3 hours", or "Until resumed". */
export const pauseChoiceText = (hours: PauseChoice): string =>
  hours === null ? "Until resumed" : `For ${hoursText(hours)}`;

/** The member's sharing in this circle, explained for a guardian, and what they can do about it. */
export interface SharingView {
  lines: string[];
  /** A guardian may pause this circle. */
  canPause: boolean;
  /** A guardian may resume this circle (a global pause still holds if there is one). */
  canResume: boolean;
  /** A guardian may start an approximate area for this circle. */
  canStartBubble: boolean;
  /** A guardian may end the approximate area for this circle. */
  canEndBubble: boolean;
}

export function sharingView(
  entry: Pick<SupervisedMember, "user_id" | "name">,
  sharing: SupervisedSharing,
  selfId: string,
  nowMs: number,
  units: Units = "metric",
  locale?: string,
): SharingView {
  const { name } = entry;
  const lines: string[] = [];
  if (sharing.paused_everywhere) {
    lines.push(`${name} paused sharing everywhere, so this circle can't see them. Only ${name} can resume that.`);
  }
  if (sharing.paused) {
    const by = sharing.paused_by;
    if (by && by.user_id !== entry.user_id) {
      const until = untilText(sharing.paused_until, nowMs, locale);
      lines.push(
        `Sharing with this circle paused by ${by.user_id === selfId ? "you" : by.name} ${until ?? "until resumed"}.`,
      );
    } else if (by) {
      lines.push(`${name} paused sharing with this circle.`);
    } else {
      lines.push("Sharing with this circle is paused.");
    }
  }
  if (sharing.bubble_everywhere) {
    lines.push(`${name} shares only an approximate area with every circle. Only ${name} can end that.`);
  }
  if (sharing.bubble) lines.push(`${approximateText(sharing.bubble, nowMs, units, locale)} with this circle.`);
  if (lines.length === 0) lines.push(`${name} shares their location with this circle.`);
  return {
    lines,
    canPause: !sharing.paused,
    canResume: sharing.paused,
    canStartBubble: !sharing.paused && !sharing.bubble,
    canEndBubble: sharing.bubble !== null,
  };
}

function circleSummaryText(circle: NonNullable<RequestSummary["circle"]>, locale?: string): string {
  if (circle.kind === "event" && circle.event) {
    const at = new Date(circle.event.starts_at).toLocaleString(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: circle.event.time_zone,
    });
    return `the event “${circle.name}” (${at})`;
  }
  const count = circle.member_count;
  const members = count === undefined ? "" : `, ${count} ${count === 1 ? "member" : "members"}`;
  return `“${circle.name}” (${circle.kind} circle${members})`;
}

function bubbleSummaryText(summary: RequestSummary, units: Units): string {
  const radius = summary.radius_m ? `within ${radiusText(summary.radius_m, units)}` : "an area";
  const hours = summary.duration_hours;
  return `${radius}, ${hours ? `for ${hoursText(hours)}` : "until they turn it off"}`;
}

/** What the member asks for, as their guardians read it. */
export function requestText(
  request: Pick<CircleRequest, "action" | "summary">,
  name: string,
  units: Units = "metric",
  locale?: string,
): string {
  const { summary } = request;
  switch (request.action) {
    case "pause_circle":
      return `${name} asks to pause sharing with this circle ${summary.pause_hours ? `for ${hoursText(summary.pause_hours)}` : "until they resume"}`;
    case "pause_global":
      return `${name} asks to pause sharing everywhere`;
    case "bubble_circle":
      return `${name} asks to share an approximate area with this circle (${bubbleSummaryText(summary, units)})`;
    case "bubble_everywhere":
      return `${name} asks to share an approximate area with every circle (${bubbleSummaryText(summary, units)})`;
    case "leave":
      return `${name} asks to leave this circle`;
    case "delete_account":
      return `${name} asks to delete their account`;
    case "join_circle":
      return `${name} asks to join ${summary.circle ? circleSummaryText(summary.circle, locale) : "another circle"}`;
    case "create_circle":
      if (!summary.circle) return `${name} asks to create a circle`;
      return summary.circle.kind === "event"
        ? `${name} asks to create the event “${summary.circle.name}”`
        : `${name} asks to create the ${summary.circle.kind} circle “${summary.circle.name}”`;
    default:
      return `${name} asks for something this version of the app doesn't know`;
  }
}

const FAILURES: Record<string, string> = {
  circle_full: "the circle is full",
  not_found: "the invite no longer works",
  invite_expired: "the invite no longer works",
  already_member: "they're already in that circle",
  sos_open: "they have an SOS open",
  event_limit: "they already organise as many events as allowed",
  last_admin: "they're the circle's only admin",
  event_ended: "the event has ended",
  sharing_paused: "their sharing is paused",
  approximate_sharing: "they share only an approximate area",
};

/** Why an approved request couldn't be carried out. */
export const failureText = (code: string | null | undefined): string =>
  (code && FAILURES[code]) ?? "the server refused it";

/** What became of a request the caller just decided. */
export function decisionText(request: Pick<CircleRequest, "status" | "error" | "approval">, name: string): string {
  switch (request.status) {
    case "pending":
      return request.approval
        ? "Approved here. It also needs a guardian's approval in another circle."
        : "Still waiting for a decision.";
    case "approved":
      return `Approved. ${name}'s request has been carried out.`;
    case "failed":
      return `Approved, but it couldn't be done: ${failureText(request.error)}. ${name} can ask again.`;
    case "declined":
      return `Declined. ${name} is told.`;
    case "stale":
      return staleText(name);
    case "expired":
      return "This request expired.";
    case "cancelled":
      return `${name} withdrew this request.`;
    default:
      return "This request is closed.";
  }
}

const staleText = (name: string) =>
  `${name}'s supervision changed since they asked, so this request no longer applies. They can ask again.`;

/** Approve/decline errors worth explaining; null for the generic message. */
export function decideErrorText(code: string | undefined, name: string): string | null {
  if (code === "request_stale") return staleText(name);
  if (code === "request_closed") return "This request was already decided, withdrawn or has expired.";
  return null;
}

/** Supervision write errors worth explaining; null for the generic message. */
export function supervisionErrorText(code: string | undefined, name: string): string | null {
  switch (code) {
    case "supervised_member":
      return `${name} is an admin. Only members who aren't admins can be supervised.`;
    case "supervision_unavailable":
      return "This circle can't use supervision right now.";
    case "sos_open":
      return `${name} has an SOS open, so their sharing can't be made approximate until it's resolved.`;
    case "validation_failed":
      return "Guardians must be admins of this circle who aren't supervised themselves.";
    default:
      return null;
  }
}

function str(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === "string" ? value : null;
}

function num(data: Record<string, unknown>, key: string): number | null {
  const value = data[key];
  return typeof value === "number" ? value : null;
}

const NOTICE_PHRASES: Partial<Record<Action, string>> = {
  pause_circle: "paused sharing with this circle",
  pause_global: "paused sharing everywhere",
  bubble_circle: "started sharing an approximate area with this circle",
  bubble_everywhere: "started sharing an approximate area with every circle",
  leave: "left the circle",
  join_circle: "joined another circle",
  create_circle: "created a circle",
};

const ACTION_PHRASES: Record<Action, string> = {
  pause_circle: "pause sharing with this circle",
  pause_global: "pause sharing everywhere",
  bubble_circle: "share an approximate area with this circle",
  bubble_everywhere: "share an approximate area with every circle",
  leave: "leave the circle",
  delete_account: "delete their account",
  join_circle: "join another circle",
  create_circle: "create a circle",
};

const actionPhrase = (data: Record<string, unknown>, self = false) => {
  const phrase = ACTION_PHRASES[str(data, "action") as Action] ?? "do something";
  return self ? phrase.replace("their", "your") : phrase;
};

const DROP_REASONS: Record<string, string> = {
  demoted: "is no longer an admin",
  left: "left the circle",
  removed: "was removed from the circle",
  deleted: "deleted their account",
};

/**
 * One line of the activity log, or null for an event this app doesn't know.
 * `nameOf` resolves user ids named in `data` (guardians); the caller reads as "You".
 */
export function logText(
  entry: Pick<LogEntry, "event" | "actor" | "data"> & { member_id?: string },
  memberName: string,
  selfId: string,
  nameOf: (userId: string) => string | undefined,
  locale?: string,
  units: Units = "metric",
): string | null {
  const data = entry.data;
  const actor = entry.actor ? (entry.actor.user_id === selfId ? "You" : entry.actor.name) : null;
  const someone = actor ?? "Someone";
  // The member's own log reads in the second person.
  const self = entry.member_id !== undefined && entry.member_id === selfId;
  const member = self ? "You" : memberName;
  const memberObj = self ? "you" : memberName;
  const memberPoss = self ? "your" : `${memberName}'s`;
  const MemberPoss = self ? "Your" : `${memberName}'s`;
  const was = self ? "were" : "was";
  switch (entry.event) {
    case "offered":
      return `${actor ?? "An admin"} offered supervision`;
    case "offer_withdrawn":
      return str(data, "reason") === "promoted"
        ? `The offer was withdrawn when ${memberObj} became an admin`
        : `${actor ?? "An admin"} withdrew the offer`;
    case "offer_declined":
      return `${member} declined the offer`;
    case "offer_expired":
      return "The offer expired";
    case "started":
      return str(data, "via") === "invite"
        ? `${member} joined with a supervised invite`
        : `${member} accepted supervision`;
    case "accepted":
      return `${member} accepted the changes`;
    case "changed":
      return `${actor ?? "A guardian"} loosened the controls`;
    case "guardian_dropped": {
      const id = str(data, "guardian_id");
      const who = (id && (id === selfId ? "You" : nameOf(id))) ?? "A guardian";
      const why = DROP_REASONS[str(data, "reason") ?? ""] ?? "left";
      return `${who} ${why} and is no longer a guardian`;
    }
    case "release_started": {
      const at = str(data, "release_at");
      return `${member} asked to stop supervision${at ? `; it ends on ${dateText(at, locale)}` : ""}`;
    }
    case "release_cancelled":
      return `${member} decided to keep supervision`;
    case "ended":
      switch (str(data, "reason")) {
        case "released":
          return `Supervision ended: ${memberPoss} notice period ran out`;
        case "no_guardian":
          return "Supervision ended: no guardian was left";
        case "left":
          return `${member} left the circle, so supervision ended`;
        case "removed":
          return `${member} ${was} removed from the circle, so supervision ended`;
        case "promoted":
          return `${member} became an admin, so supervision ended`;
        default:
          return `${actor ?? "A guardian"} ended supervision`;
      }
    case "paused": {
      const until = str(data, "until");
      const end = until
        ? `until ${new Date(until).toLocaleString(locale, { weekday: "short", hour: "2-digit", minute: "2-digit" })}`
        : "until resumed";
      return `${someone} paused ${memberPoss} sharing with this circle ${end}`;
    }
    case "resumed":
      return str(data, "reason") === "expired"
        ? `The timed pause ran out; ${memberObj} ${self ? "share" : "shares"} with this circle again`
        : `${someone} resumed ${memberPoss} sharing with this circle`;
    case "bubble_started": {
      const radius = num(data, "radius_m");
      const hours = num(data, "duration_hours");
      const area = radius ? ` within ${radiusText(radius, units)}` : "";
      return `${someone} started sharing an approximate area${area} for ${memberObj}${hours ? ` for ${hoursText(hours)}` : ""}`;
    }
    case "bubble_ended":
      return `${someone} ended ${memberPoss} approximate area`;
    case "request_created":
      return `${member} asked to ${actionPhrase(data, self)}`;
    case "request_cancelled":
      return `${member} withdrew a request to ${actionPhrase(data, self)}`;
    case "request_approved":
      return `${actor ?? "A guardian"} approved ${memberPoss} request to ${actionPhrase(data, self)}`;
    case "request_declined":
      return `${actor ?? "A guardian"} declined ${memberPoss} request to ${actionPhrase(data, self)}`;
    case "request_stale":
      return `${MemberPoss} request to ${actionPhrase(data, self)} no longer applied after supervision changed`;
    case "request_executed":
      return `Done: ${memberPoss} request to ${actionPhrase(data, self)}`;
    case "request_failed":
      return `${MemberPoss} approved request to ${actionPhrase(data, self)} couldn't be done: ${failureText(str(data, "error"))}`;
    case "notice": {
      const action = str(data, "action") as Action | null;
      const name = str(data, "name");
      if ((action === "join_circle" || action === "create_circle") && name) {
        return `${member} ${action === "join_circle" ? "joined" : "created"} “${name}”`;
      }
      return `${member} ${(action && NOTICE_PHRASES[action]) ?? "did something guardians are told about"}`;
    }
    case "signed_out":
      return `${member} ${self ? "have" : "has"} no signed-in device`;
    default:
      return null;
  }
}

/** `POST /circles/{id}/invites`'s body; a supervised invite always works once. */
export function inviteCreateBody(
  ttlHours: number,
  maxUses: number | null,
  supervision: SupervisionDraft | null,
  available: readonly string[],
): InviteCreate {
  if (!supervision) return { ttl_hours: ttlHours, max_uses: maxUses };
  return {
    ttl_hours: ttlHours,
    max_uses: 1,
    supervision: { controls: controlsBody(supervision.controls, available), guardian_ids: supervision.guardianIds },
  };
}
